import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Experience from "../Experience";
import type { CoolS2D } from "./CoolS2D";
import type CoolS3D from "./CoolS3D";
import Earth from "./Earth";
import PaperBackgroundV2 from "./PaperBackgroundV2";
import SpaceBackground from "./SpaceBackground";

export class SceneDirector {
  private coolS2D: CoolS2D;
  private coolS3D: CoolS3D;
  private paperBackground: PaperBackgroundV2;
  private spaceBackground: SpaceBackground;
  private earth: Earth;
  private spaceMode = false;
  private isAnimating = false;
  private experience: Experience;
  private introAnimationTime: number;
  private sections: HTMLElement[];

  constructor(coolS2D: CoolS2D, coolS3D: CoolS3D, sections: HTMLElement[]) {
    this.coolS2D = coolS2D;
    this.coolS3D = coolS3D;
    this.sections = sections;

    this.experience = new Experience();
    this.paperBackground = new PaperBackgroundV2();
    // this.paperBackground.show();
    this.spaceBackground = new SpaceBackground();
    this.earth = new Earth();

    // Hide 3D Cool S by default until space mode is entered
    this.coolS3D.setVisibility(false);
    this.coolS2D.group.visible = true;
    this.introAnimationTime = 7;
    this.setScrollAnimation();
  }

  enterSpaceMode() {
    if (this.spaceMode || this.isAnimating) return;
    this.spaceMode = true;
    this.isAnimating = true;

    // Hide scroll arrow when entering space mode
    const scrollArrow = (this.experience as any).scrollArrow;
    if (scrollArrow && typeof scrollArrow.enterSpaceMode === "function") {
      scrollArrow.enterSpaceMode();
    }

    const paperCrumbleDuration = 12;

    this.paperBackground.playCrumpleAnimation(paperCrumbleDuration, () => {
      this.paperBackground.setVisibility(false);
      // Dispose PaperBackground after it's invisible
      this.paperBackground.dispose();
      // After paper implodes, show and scale up Earth
      this.earth.load(() => {
        // wait a second to load the Cool S as the Earth might load with transparent tiles.
        setTimeout(
          () => this.coolS3D.showUp(this.earth.globe!.position, 4),
          1000
        );
        this.enableOrbitControls();
        // Trigger space mode text animation after earth loads
        this.animateSpaceModeText();
      });
    });

    this.animateCameraReveal();

    const timeline = gsap.timeline({
      onComplete: () => {
        this.isAnimating = false;
      },
    });

    // 2D Cool S crumble dissolve animation (starts at same time as paper)
    timeline.call(
      () => {
        this.coolS2D.crumbleDissolve(paperCrumbleDuration).then(() => {
          this.coolS2D.group.visible = false;
          // Dispose CoolS2D after it's invisible
          this.coolS2D.dispose();
        });
      },
      [],
      0
    );

    // Space background stars fade in (starts at same time)
    const spaceOpacity = { value: 0 };
    timeline.to(
      spaceOpacity,
      {
        value: 1,
        duration: this.introAnimationTime,
        ease: "power1.inOut",
        onStart: () => {
          this.spaceBackground.show();
          this.spaceBackground.setOpacity(0);
        },
        onUpdate: () => {
          this.spaceBackground.setOpacity(spaceOpacity.value);
        },
      },
      0
    );
  }

  private animateSpaceModeText() {
    // Fade out sections first
    gsap.to(this.experience.sectionsRoot, {
      opacity: 0,
      duration: 2,
      ease: "power2.inOut",
      onComplete: () => {
        // Show space mode text after sections finish fading
        const spaceModeText = document.getElementById("space-mode-text");
        if (spaceModeText) {
          spaceModeText.classList.remove("display-none");
          // Small delay before adding visible class for fade-in transition
          setTimeout(() => {
            spaceModeText.classList.add("visible");
          }, 100);
        }
      },
    });
  }

  private animateCameraReveal() {
    const camera = this.experience.camera.instance;
    const initialX = camera.position.x;

    const cameraAnim = gsap.timeline();

    // Calculate movement based on screen width/aspect ratio
    const aspectRatio = window.innerWidth / window.innerHeight;
    const isMobile = window.innerWidth < 768;

    // Scale horizontal movement: less on narrow/mobile screens, more on wide screens
    const xMovement = isMobile ? 0 : Math.min(10, aspectRatio * 6);
    const zMovement = 600;

    // Zoom out slightly
    cameraAnim.to(
      camera.position,
      {
        z: zMovement,
        duration: 15,
        ease: "linear",
      },
      0
    );

    // Rotate camera to the right to show depth
    cameraAnim.to(
      camera.position,
      {
        x: initialX + xMovement,
        duration: 5,
        ease: "power2.inOut",
        onUpdate: () => {
          // Keep camera looking at the CoolS3D
          camera.lookAt(0, 0, 0.05);
        },
      },
      0
    );
  }

  exitSpaceMode() {
    // Prevent going back once in space mode
    return;
  }

  isInSpaceMode() {
    return this.spaceMode;
  }

  getEarth(): Earth {
    return this.earth;
  }

  update(deltaMs: number) {
    // Update paper background animation
    this.paperBackground.update(deltaMs / 1000);

    // Update space background (comets, etc.)
    if (this.spaceMode) {
      this.spaceBackground.update(deltaMs / 1000); // Convert to seconds
    }
    this.earth.update();
  }

  private enableOrbitControls() {
    // Enable pointer events on canvas, disable on sections
    const canvas = this.experience.canvas;
    canvas.classList.remove("non-scrollable");
    canvas.classList.add("scrollable");

    const sectionsRoot = this.experience.sectionsRoot;
    sectionsRoot.classList.remove("scrollable");
    sectionsRoot.classList.add("non-scrollable");

    // Show UI overlay (for upload button, etc.)
    const uiOverlay = document.getElementById("ui-overlay");
    if (uiOverlay) {
      uiOverlay.classList.add("visible");
    }

    // Enable and configure camera orbit controls
    this.experience.camera.enableOrbitControls(this.earth.globe!);
  }

  setScrollAnimation() {
    /**
     * Sections
     */
    const coolS2D = this.coolS2D!;

    for (let i = 0; i < this.sections.length; i++) {
      const sec = this.sections[i];
      const next = this.sections[i + 1] as HTMLElement | undefined;
      const content = sec.querySelector(".content") as HTMLElement;
      const isLast = i === this.sections.length - 1; // Last scrollable section
      const isFirst = i === 0;

      ScrollTrigger.create({
        trigger: sec,
        start: "top top", // progress starts at 0 exactly when the section hits the top
        endTrigger: next ?? sec,
        end: next ? "top top" : "max",
        scrub: true, // tie to scroll position
        pin: true,
        pinSpacing: isLast,
        anticipatePin: 1,
        onEnter: () => {
          // ensure it’s visible at start
          content.style.display = "inline-block";
        },
        onUpdate: (self) => {
          const p = self.progress; // 0..1 within the section
          const reveal = isFirst ? 100 : Math.min(p * 2, 1); // first half reveals
          const fadeP = Math.max((p - 0.5) * 2, 0); // second half fades

          // 1) reveal: shrink right inset 100% -> 0%

          content.style.setProperty("--revealRight", `${(1 - reveal) * 100}%`);

          // 2) opacity fade (no spatial gradient). Exponential-ish:
          //    opacity = 1 - (fadeP ^ k).
          const k = 4; // tweak to taste
          const opacity = isLast ? 1 : 1 - Math.pow(fadeP, k);
          content.style.opacity = String(opacity);

          coolS2D.setVisibility(i, p);
        },
        onLeave: () => {
          if (!isLast) {
            content.style.display = "none";
          } else {
            sec.className = "section-panel is-active";
            this.enterSpaceMode();
          }
          coolS2D.setVisibility(i, 1);
        },
        onEnterBack: () => {
          content.style.display = "inline-block";
        },
        onLeaveBack: () => {
          content.style.display = "none";
          coolS2D.setVisibility(i, 0);
          if (isLast) {
            this.exitSpaceMode();
          }
        },
        toggleClass: { targets: sec, className: "is-active" },
      });
    }
    ScrollTrigger.refresh();
  }
}

export default SceneDirector;
